import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { useEffect, useRef } from "react";

export default function ThreeViewer({ src, width = "100%", height = "600px", transparent = false, distance=50, xPi=67, yPi=36 }) {
  const containerRef = useRef();
  const sceneRef = useRef();
  const cameraRef = useRef();
  const rendererRef = useRef();
  const controlsRef = useRef();
  const objectRef = useRef();

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const scene = new THREE.Scene();
    scene.background = transparent ? null : new THREE.Color(0xffffff);
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(distance, container.clientWidth / container.clientHeight, 1, 1000);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: transparent });
    renderer.setSize(container.clientWidth, container.clientHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.outputEncoding = THREE.SRGBColorSpace;
    renderer.toneMappingExposure = 1.2;
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.enableZoom = false;
    controls.minPolarAngle = Math.PI / 2;
    controls.maxPolarAngle = Math.PI / 2;
    controlsRef.current = controls;

    // 💡 Luces (creadas antes, apuntan al modelo más adelante)
    const hemiLight = new THREE.HemisphereLight(0xffffff, 0x444444, 9);
    hemiLight.position.set(0, 5, 0);
    scene.add(hemiLight);

    const dirLight1 = new THREE.DirectionalLight(0xffffff, 6);
    dirLight1.position.set(5, 5, 5);
    scene.add(dirLight1);

    const dirLight2 = new THREE.DirectionalLight(0xffffff, 4);
    dirLight2.position.set(-5, 3, -3);
    scene.add(dirLight2);

    const backLight = new THREE.DirectionalLight(0xffffff, 3);
    backLight.position.set(0, 3, -5); // detrás del modelo (eje Z negativo)
    backLight.target.position.set(0, 0, 0); // apunta al centro del modelo
    scene.add(backLight);

    const ambient = new THREE.AmbientLight(0xffffff, 4);
    scene.add(ambient);

    // Guardamos las luces direccionales para usarlas luego
    scene.userData.dirLight1 = dirLight1;
    scene.userData.dirLight2 = dirLight2;
    scene.userData.hemiLight = hemiLight;
    scene.userData.backLight = backLight;

    let rafId;
    const animate = () => {
      controls.update();
      renderer.render(scene, camera);
      rafId = requestAnimationFrame(animate);
    };
    animate();

    const resizeObserver = new ResizeObserver(() => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      if (w > 0 && h > 0) {
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
        renderer.setSize(w, h);
      }
    });
    resizeObserver.observe(container);

    return () => {
      cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
      controls.dispose();
      renderer.dispose();
      if (container && renderer.domElement) container.removeChild(renderer.domElement);
    };
  }, [transparent]);

  useEffect(() => {
    const scene = sceneRef.current;
    const camera = cameraRef.current;
    const renderer = rendererRef.current;
    if (!src || !scene || !camera || !renderer) return;

    if (objectRef.current) {
      objectRef.current.traverse((child) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (Array.isArray(child.material)) child.material.forEach((m) => m.dispose());
          else child.material.dispose();
        }
      });
      scene.remove(objectRef.current);
      objectRef.current = null;
    }

    const ext = src.split(".").pop().toLowerCase();
    if (ext === "gltf" || ext === "glb") {
      const loader = new GLTFLoader();
      loader.load(
        src,
        (gltf) => {
          const model = gltf.scene;

          // Crear un grupo pivot
          const pivot = new THREE.Group();
          pivot.add(model);
          scene.add(pivot);

          // Calcular los límites del modelo
          const box = new THREE.Box3().setFromObject(model);
          const size = new THREE.Vector3();
          const center = new THREE.Vector3();
          box.getSize(size);
          box.getCenter(center);

          // 📦 Mover el modelo para que su centro quede en el origen del pivot
          // Esto centra el eje de rotación en el punto medio (centro geométrico)
          model.position.sub(center);

          // Ajustar posición del pivot para mantener el modelo visible
          pivot.position.copy(center);

          // Ajuste automático de cámara
          const maxDim = Math.max(size.x, size.y, size.z);
          const fov = camera.fov * (Math.PI / 180);
          const distance = maxDim / (2 * Math.tan(fov / 2));
          camera.position.set(center.x, center.y, center.z + distance * 1.5);
          camera.lookAt(center);
          controlsRef.current.target.copy(center);
          controlsRef.current.update();

          // 🔄 Animacion
          const initialRotation = 0;
          const finalRotation = (xPi * Math.PI) / yPi; 
          const duration = 1000;
          const start = performance.now();

          function rotateModel(time) {
            const elapsed = time - start;
            const progress = Math.min(elapsed / duration, 1);
            pivot.rotation.y = initialRotation + finalRotation * progress; // antihorario
            if (progress < 1) requestAnimationFrame(rotateModel);
          }

          requestAnimationFrame(rotateModel);
        },
        undefined,
        (err) => console.error("GLTF Load error:", err)
      );
    }
  }, [src]);

  return (
    <div
      ref={containerRef}
      style={{
        width,
        height,
        maxWidth: "600px",
        maxHeight: "500px",
        marginLeft: "5rem",
        marginTop: "-2rem",
      }}
    />
  );
}
